import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks } from "./helpers";
import { vendorUUIDs } from "../../constants";

import StaticSeatMap from "public/images/saintpaulsaints/2025SeatingMap.jpg";
import FindGamesBanner from "public/images/saintpaulsaints/Securian-Financial-Mini-Plan-Attend-Offer_1486x148.jpg";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/St._Paul_Saints.png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "saintpaulsaints_flex",
  browserTitle: "St. Paul Saints",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "St. Paul Saints",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 1,
        updatedAccountGroup: 0,
        discount: 0,
        customProgressBarMessage:
          "Congrats - you've unlocked $140 per ticket for the Securian Financial Club! Add 2-4 more games for $130 a ticket, 5-7 for $120 a ticket, or 8+ for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
      },
      {
        id: 2,
        minSelectedAmount: 2,
        updatedAccountGroup: 1,
        discount: 8,
        customProgressBarMessage:
          "Congrats - you've unlocked $130 per ticket for the Securian Financial Club! Add 1-3 more games at $130 a ticket, 4-6 for $120 a ticket, or 7+ for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
      },
      {
        id: 3,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        discount: 8,
        customProgressBarMessage:
          "Congrats - you've unlocked $130 per ticket for the Securian Financial Club! Add 1-2 more games at $130 a ticket, 3-5 for $120 a ticket, or 6+ for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
        hideOfferFromModal: true,
      },
      {
        id: 4,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 8,
        customProgressBarMessage:
          "Congrats - you've unlocked $130 per ticket for the Securian Financial Club! Add 1 more game at $130 a ticket, 2-4 for $120 a ticket, or 5+ for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
        hideOfferFromModal: true,
      },
      {
        id: 5,
        minSelectedAmount: 5,
        updatedAccountGroup: 1,
        discount: 8,
        customProgressBarMessage:
          "Congrats - you've unlocked $130 per ticket for the Securian Financial Club! Add 1-3 more games for $120 a ticket, or 4+ for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
        hideOfferFromModal: true,
      },
      {
        id: 6,
        minSelectedAmount: 6,
        updatedAccountGroup: 2,
        discount: 15,
        customProgressBarMessage:
          "Congrats - you've unlocked $120 per ticket for the Securian Financial Club! Add 3+ games for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
      },
      {
        id: 7,
        minSelectedAmount: 7,
        updatedAccountGroup: 2,
        discount: 15,
        customProgressBarMessage:
          "Congrats - you've unlocked $120 per ticket for the Securian Financial Club! Add 2+ games for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
        hideOfferFromModal: true,
      },
      {
        id: 8,
        minSelectedAmount: 8,
        updatedAccountGroup: 2,
        discount: 15,
        customProgressBarMessage:
          "Congrats - you've unlocked $120 per ticket for the Securian Financial Club! Add 1 more game for $100 per ticket and access to an exclusive Banana Ball purchasing opportunity!",
        hideOfferFromModal: true,
      },
      {
        id: 9,
        minSelectedAmount: 9,
        updatedAccountGroup: 3,
        discount: 30,
        customProgressBarMessage:
          "Congrats - you've unlocked $100 per ticket for the Securian Financial Club & access to an exclusive Banana Ball purchasing opportunity! Checkout now.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    maxGameSelectionCount: 13,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://sp6.glitnirticketing.com/spticket/web/login.php?ret_link=%2Fspticket%2Fweb%2Fwportal.php&type=notLogged",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: true,
      showLogoWithTitle: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:651-644-6659"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (651) 644-6659
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#00539f",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Add 1 game at our Securian Financial Club for $140 per ticket! The more games you add - the higher your ticket discount. Unlock the opportunity to purchase Banana Ball tickets for one of two games in August 2025 after selecting 9+ games!",
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your St. Paul Saints tickets are confirmed! Your tickets will be
        available within 24 hours in your{" "}
        <Link
          target="_blank"
          href="https://sp6.glitnirticketing.com/spticket/web/login.php?ret_link=%2Fspticket%2Fweb%2Fwportal.php&type=notLogged"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          ProVenue
        </Link>{" "}
        account.
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
